import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { Suspense, useEffect, useState, useCallback } from "react"; // 1. Adicionar useCallback
import {
  getStockInventory,
  getStockInventoryAuditReportDetails,
  getStockInventoryReportDetails,
} from "@/common/services/stock-inventory.api";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { STATUS_LABELS, StockInventory } from "./Types/StockInventory.types";
import ModalCreateStockInventory from "./Modals/StockInventory.modal";
import {
  generateInventoryExcel,
  generateInventoryPdf,
  generateAuditPdf,
} from "@/common/helpers/report.helpers";
import { ActionItem } from "@/common/components/ui/context-menu/PopoverList";
import ModalConfirmCount from "./Modals/StockInventoryConfirm.modal";
import { getCompanyId } from "@/common/services/company.api";
import { useCompanyStore } from "@/common/states/store/Company";

const headers = {
  companyId: "Cidade",
  createdAt: "Criado em",
  author: "Criado por",
  status: "Situação",
};
const EMPTY_ACTION = { eventName: "", data: undefined };

export function StockInventorytable() {
  const { notifyError, notifySuccess } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [stockInventorys, setStockInventorys] = useState<StockInventory[]>([]);
  const companyId = getCompanyId();
  const companyStore = useCompanyStore();

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
  });

  const handleGenerateReport = async (
    inventoryId: string,
    format: "pdf" | "excel"
  ) => {
    setIsLoading(true);
    try {
      const result = await getStockInventoryReportDetails(inventoryId);

      if (result.ok && result.data) {
        if (format === "pdf") {
          await generateInventoryPdf(result.data);
        } else {
          await generateInventoryExcel(result.data);
        }
        notifySuccess(`Relatório ${format.toUpperCase()} gerado com sucesso!`);
      } else {
        notifyError(
          result.message || `Falha ao buscar dados para o relatório.`
        );
      }
    } catch (error) {
      notifyError(
        (error as Error).message || `Ocorreu um erro ao gerar o relatório.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateAuditReport = async (inventoryId: string) => {
    setIsLoading(true);
    try {
      const result = await getStockInventoryAuditReportDetails(inventoryId); // <-- USA A NOVA API

      if (result.ok && result.data) {
        await generateAuditPdf(result.data); // <-- USA O NOVO HELPER DE PDF
        notifySuccess(`Relatório de Auditoria gerado com sucesso!`);
      } else {
        notifyError(
          result.message || `Falha ao buscar dados para a auditoria.`
        );
      }
    } catch (error) {
      notifyError(
        (error as Error).message || `Ocorreu um erro ao gerar a auditoria.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const actions: ActionItem[] = [
    {
      label: "Gerar PDF",
      action: (data: unknown) => {
        const inventory = data as StockInventory;
        handleGenerateReport(inventory._id, "pdf");
      },
      visible: (data: unknown) => {
        const inventory = data as StockInventory;
        return inventory.status !== "completed";
      },
    },
    {
      label: "Gerar Excel",
      action: (data: unknown) => {
        const inventory = data as StockInventory;
        handleGenerateReport(inventory._id, "excel");
      },
      visible: (data: unknown) => {
        const inventory = data as StockInventory;
        return inventory.status !== "completed";
      },
    },
    {
      label: "Confirmar Contagem",
      action: (data: unknown) => {
        const inventory = data as StockInventory;
        setAction({ eventName: "confirm-count", data: inventory });
      },
      visible: (data: unknown) => {
        const inventory = data as StockInventory;
        return inventory.status !== "completed";
      },
    },
    {
      label: "Auditoria",
      action: (data: unknown) => {
        const inventory = data as StockInventory;
        handleGenerateAuditReport(inventory._id);
      },
      visible: (data: unknown) => {
        const inventory = data as StockInventory;
        return inventory.status !== "pending";
      },
    },
  ];
  const isAction = (eventName: string) => action.eventName === eventName;
  const loadStockInventory = useCallback(async () => {
    if (!companyId || companyStore.selectedUf) {
      setStockInventorys([]);
      setData((prev) => ({ ...prev, totalItems: 0 }));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getStockInventory({
        search: filters.search,
        status: filters.status,
        page,
        itemsPerPage: filters.itemsPerPage,
      });

      if (result && !result.ok) {
        notifyError(
          (result as { message?: string })?.message || "Erro desconhecido"
        );
        return;
      }

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      setStockInventorys(result.data);
    } catch (error) {
      notifyError("Falha ao carregar dados do inventário.");
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, data.itemsPerPage, notifyError, companyId, companyStore]);

  useEffect(() => {
    loadStockInventory();
  }, [loadStockInventory]);

  const handleFilterChange = (newFilters: TableFiltersState) => {
    setPage(1);
    setFilters(newFilters);
  };

  const handleSave = () => {
    setAction({ eventName: "undefined", data: undefined });
    loadStockInventory();
  };
  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };
  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Inventário de Estoque"
          placeholder="Digite a Id, a cidade ou o Criado por."
          statusOptions={{ pending: "Pendente", completed: "Concluído" }}
          newItemLabel="Novo Inventário"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="stockInventory"
          tableData={stockInventorys as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={stockInventorys as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={(newFilters) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              ...newFilters,
            }));
          }}
          renders={
            {
              companyId: (companyId: string) =>
                getCompanySigla(companyId as string),
              createdAt: (date: string) =>
                new Date(date).toLocaleDateString("pt-BR"),
              status: (status: string) => STATUS_LABELS[status] || status,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="vehicles"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>

      {isAction("create") && (
        <ModalCreateStockInventory
          onClose={cleanActionData}
          onSave={handleSave}
        />
      )}
      {isAction("confirm-count") && (
        <ModalConfirmCount
          inventory={action.data as StockInventory}
          onClose={cleanActionData}
          onSave={handleSave}
        />
      )}
    </Suspense>
  );
}
